import { Button, Modal, modalWidth } from "@hubrise/react-components"
import * as React from "react"
import { useTranslation } from "react-i18next"

import useCatalogNavigation from "Catalog/shared/hooks/useCatalogNavigation"
import Table, { Footer } from "shared/components/Table"
import useEditor from "shared/hooks/useEditor"
import useNavigateNoUpdate from "shared/hooks/useNavigateNoUpdate"
import { IDeal, IDealLine, IDealLineSku } from "shared/hubrise/models"
import * as Sku from "shared/hubrise/utils/Sku"

import AttachSku from "../AttachSku"
import { SkuMap } from "../types"

import { FieldExtraCharge, SkuName } from "./Styles"

interface DealLineSkusProps {
  deal: IDeal
  dealLine: IDealLine
  skuMap: SkuMap
}

const DealLineSkus = ({ deal, dealLine, skuMap }: DealLineSkusProps): JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigateNoUpdate()

  const { editor, catalog } = useEditor()
  const { currency } = catalog
  const { catalogNavigation } = useCatalogNavigation()

  const handleReorder = (newDealLineSkus: Array<IDealLineSku>): void => {
    editor.dealLine.reorderSkus(deal.id, dealLine.id, newDealLineSkus)
  }

  const handleDelete = (dealLineSku: IDealLineSku): void =>
    editor.dealLineSku.delete(deal.id, dealLine.id, dealLineSku.id)

  const canDelete = dealLine.skus.length >= 2

  const skuName = (dealLineSku: IDealLineSku): string => {
    const { product, sku } = skuMap.get(dealLineSku.skuId)!
    return Sku.fullName(product, sku)
  }

  const goToProduct = (dealLineSku: IDealLineSku): void => {
    const { product } = skuMap.get(dealLineSku.skuId)!
    navigate(catalogNavigation.editPath("product", product.id))
  }

  return (
    <>
      <Table
        items={dealLine.skus}
        columns={[
          {
            header: t("deal.lines.skuName"),
            cellRenderer: (dealLineSku) => (
              <SkuName onClick={() => goToProduct(dealLineSku)}>{skuName(dealLineSku)}</SkuName>
            ),
          },
          {
            header: t("deal.lines.extra"),
            cellRenderer: (dealLineSku) => (
              <FieldExtraCharge
                initialValue={dealLineSku.extraCharge}
                onChange={(value) =>
                  editor.dealLineSku.update(deal.id, dealLine.id, dealLineSku.id, "extraCharge", value)
                }
                currency={currency}
              />
            ),
          },
        ]}
        onReorder={handleReorder}
        onDelete={canDelete ? handleDelete : undefined}
        droppableId={`dealLine_${dealLine.id}`}
      />

      <Footer>
        <Modal
          modalWidth={modalWidth.medium}
          renderLink={({ open }) => (
            <Button icon="add" variant="empty" onClick={open}>
              {" "}
              {t("deal.skus.attach")}{" "}
            </Button>
          )}
          renderContent={({ close }) => <AttachSku onClose={close} {...{ deal, dealLine }} />}
        />
      </Footer>
    </>
  )
}

export default DealLineSkus
